import { useEffect, useRef } from 'react';

// bpmn-js is browser-only and its ESM entry (bpmn-js/lib/NavigatedViewer)
// doesn't resolve under Node's SSR/prerender step. We therefore import it
// DYNAMICALLY inside the mount effect (effects never run during the
// vite-react-ssg build render), so the library is only ever loaded in the
// browser. The CSS is safe to import statically — Vite handles it in the
// client bundle.
import 'bpmn-js/dist/assets/diagram-js.css';
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn.css';
import 'bpmn-js/dist/assets/bpmn-js.css';

interface BpmnViewerProps {
  /** BPMN 2.0 XML to render. */
  xml: string;
  /** Optional class for the outer container (Tailwind sizing/borders). */
  className?: string;
  /** Filename suggested when the user clicks the download button (without ext). */
  downloadName?: string;
}

// Import XML into a live viewer instance and fit it to the container.
// Extracted so both the mount effect and the xml-change effect can call it.
async function importDiagram(viewer: any, xml: string) {
  try {
    await viewer.importXML(xml);
    viewer.get('canvas').zoom('fit-viewport', 'auto');
  } catch (err) {
    // bpmn-js sometimes warns on benign issues — only log if this is fatal
    // eslint-disable-next-line no-console
    console.error('bpmn-js importXML failed:', err);
  }
}

/**
 * Wrap bpmn-js's NavigatedViewer in React. The viewer manages its own DOM,
 * so we mount it once and re-import the XML when it changes.
 *
 * The dark background is set on the OUTER container (Tailwind), not via
 * bpmn-js theming — bpmn-js renders SVG in slate-700 strokes which read
 * fine on both light and dark backgrounds.
 */
export default function BpmnViewer({ xml, className = '', downloadName = 'workflow' }: BpmnViewerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<any>(null);

  // Mount the viewer once when the container is in the DOM. bpmn-js is
  // dynamically imported here so it never loads during SSR/prerender.
  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;
    let created: any = null;

    (async () => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore — vendor lib without complete types
      const { default: BpmnNavigatedViewer } = await import('bpmn-js/lib/NavigatedViewer');
      if (cancelled || !containerRef.current) return;
      created = new BpmnNavigatedViewer({ container: containerRef.current });
      viewerRef.current = created;
      // If XML was already provided, render it now that the viewer exists.
      if (xmlRef.current) void importDiagram(created, xmlRef.current);
    })();

    return () => {
      cancelled = true;
      if (created) created.destroy();
      viewerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the latest XML in a ref so the async mount can render it once ready.
  const xmlRef = useRef(xml);
  useEffect(() => {
    xmlRef.current = xml;
    const v = viewerRef.current;
    if (v && xml) void importDiagram(v, xml);
  }, [xml]);

  const handleDownload = () => {
    const blob = new Blob([xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${downloadName}.bpmn20.xml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className={`relative ${className}`}>
      <div
        ref={containerRef}
        className="w-full h-full bg-white dark:bg-slate-900 rounded-lg overflow-hidden"
        // bpmn-js needs the container to have a known size so its layout calc works
        style={{ minHeight: 360 }}
      />
      <button
        type="button"
        onClick={handleDownload}
        className="absolute top-2 right-2 text-xs px-3 py-1.5 rounded-md bg-slate-900/80 hover:bg-slate-900 text-white backdrop-blur-sm transition-colors"
        title="Download BPMN 2.0 XML"
      >
        Download .bpmn
      </button>
    </div>
  );
}
